import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  model,
  output,
  signal,
  viewChildren,
} from '@angular/core';

import type { EsnSpaceTreeMode, EsnSpaceTreeNode } from '../../../shared/types/domain';
import type { EsnTreeRowKind } from '../../../shared/types/molecules';
import { TreeRowComponent } from '../../molecules/tree-row/tree-row.component';

/**
 * One visible line of the tree, in display order.
 */
interface VisibleRow {
  node: EsnSpaceTreeNode;
  level: number;
  parentId: string | null;
  container: boolean;
  firstChildId: string | null;
}

const isContainer = (node: EsnSpaceTreeNode) => node.kind === 'world' || node.kind === 'building';

/**
 * The island's spaces as one tree, read either for their placeholders (which slots exist and
 * whether they are linked) or for the content filed under each space. Spaces stay identical
 * between modes. Arrow keys, Home/End and Enter navigate it like a native tree.
 */
@Component({
  selector: 'esn-space-tree',
  imports: [TreeRowComponent],
  templateUrl: './space-tree.component.html',
  styleUrl: './space-tree.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpaceTreeComponent {
  readonly nodes = input.required<readonly EsnSpaceTreeNode[]>();

  /**
   * Placeholders lists each space's slots, Content the assets filed under it.
   */
  readonly mode = input<EsnSpaceTreeMode>('placeholders');

  /**
   * Accessible name of the tree.
   */
  readonly label = input('Spaces');

  /**
   * Horizontal step per level in px; the Figma tree is drawn flush (0).
   */
  readonly indent = input(0);

  /**
   * Ids of the open spaces; leave unset to start with every space open.
   */
  readonly expanded = model<readonly string[] | undefined>(undefined);

  /**
   * Id of the current slot or content row.
   */
  readonly selected = model<string | null>(null);

  /**
   * A slot or content row was picked (click, Enter or Space).
   */
  readonly activated = output<EsnSpaceTreeNode>();

  private readonly rowRefs = viewChildren(TreeRowComponent, { read: ElementRef });
  private readonly focusedId = signal<string | null>(null);

  private readonly openIds = computed(() => {
    const explicit = this.expanded();
    return new Set(explicit ?? this.allContainerIds());
  });

  protected readonly rows = computed<VisibleRow[]>(() => {
    const mode = this.mode();
    const open = this.openIds();
    const out: VisibleRow[] = [];
    const shown = (node: EsnSpaceTreeNode) =>
      isContainer(node) ||
      (mode === 'placeholders' ? node.kind === 'slot-linked' || node.kind === 'slot-empty' : node.kind === 'content');

    const walk = (nodes: readonly EsnSpaceTreeNode[], level: number, parentId: string | null) => {
      for (const node of nodes) {
        if (!shown(node)) continue;
        const container = isContainer(node);
        const kids = (node.children ?? []).filter(shown);
        out.push({ node, level, parentId, container, firstChildId: kids[0]?.id ?? null });
        if (container && open.has(node.id)) walk(kids, level + 1, node.id);
      }
    };
    walk(this.nodes(), 0, null);
    return out;
  });

  /**
   * The one row in the Tab order: the focused row, else the selected one, else the first.
   */
  protected readonly tabStopId = computed(() => {
    const rows = this.rows();
    const candidates = [this.focusedId(), this.selected()];
    return candidates.find((id) => id && rows.some((r) => r.node.id === id)) ?? rows[0]?.node.id ?? null;
  });

  protected rowKind(row: VisibleRow): EsnTreeRowKind {
    const current = row.node.id === this.selected();
    switch (row.node.kind) {
      case 'world':
        return 'world';
      case 'building':
        return row.node.chip ? 'accent' : 'building';
      case 'slot-linked':
        return current ? 'slot-active' : 'slot-linked';
      case 'slot-empty':
        return current ? 'slot-active-empty' : 'slot-empty';
      case 'content':
        return current ? 'content-active' : 'content';
    }
  }

  protected isOpen(id: string): boolean {
    return this.openIds().has(id);
  }

  protected setOpen(id: string, open: boolean): void {
    const next = new Set(this.openIds());
    if (open) next.add(id);
    else next.delete(id);
    this.expanded.set([...next]);
  }

  protected onClick(row: VisibleRow): void {
    this.focusedId.set(row.node.id);
    if (row.container) this.setOpen(row.node.id, !this.isOpen(row.node.id));
    else this.pick(row.node);
  }

  protected onKeydown(event: KeyboardEvent, row: VisibleRow): void {
    const rows = this.rows();
    const index = rows.indexOf(row);
    const open = row.container && this.isOpen(row.node.id);
    let target: string | null | undefined;

    switch (event.key) {
      case 'ArrowDown':
        target = rows[index + 1]?.node.id;
        break;
      case 'ArrowUp':
        target = rows[index - 1]?.node.id;
        break;
      case 'Home':
        target = rows[0]?.node.id;
        break;
      case 'End':
        target = rows[rows.length - 1]?.node.id;
        break;
      case 'ArrowRight':
        if (row.container && !open) this.setOpen(row.node.id, true);
        else if (open) target = row.firstChildId;
        break;
      case 'ArrowLeft':
        if (open) this.setOpen(row.node.id, false);
        else target = row.parentId;
        break;
      case 'Enter':
      case ' ':
        if (row.container) this.setOpen(row.node.id, !open);
        else this.pick(row.node);
        break;
      default:
        return;
    }

    event.preventDefault();
    if (target) this.moveFocus(target);
  }

  private pick(node: EsnSpaceTreeNode): void {
    this.selected.set(node.id);
    this.activated.emit(node);
  }

  private moveFocus(id: string): void {
    this.focusedId.set(id);
    const ref = this.rowRefs().find((r) => (r.nativeElement as HTMLElement).dataset['nodeId'] === id);
    (ref?.nativeElement as HTMLElement | undefined)?.focus();
  }

  private allContainerIds(): string[] {
    const ids: string[] = [];
    const walk = (nodes: readonly EsnSpaceTreeNode[]) => {
      for (const node of nodes) {
        if (isContainer(node)) ids.push(node.id);
        walk(node.children ?? []);
      }
    };
    walk(this.nodes());
    return ids;
  }
}
